/**
 * @fileoverview Search Results Component
 * 
 * This component displays the search results for phone numbers with
 * purchase options and detailed information.
 */

import React, { useMemo } from 'react';
import { PhoneNumberContext } from '../types.js';
import { formatPhoneNumber, calculateTotalCost } from '../utils/helpers.js';

/**
 * Search Results Component
 * 
 * @param {Object} props - Component props
 * @param {PhoneNumber[]} props.results - Search results
 * @param {boolean} props.isSearching - Whether search is in progress
 * @param {function} props.onNumberSelect - Callback for number selection
 * @param {boolean} props.isPurchasing - Whether purchase is in progress
 * @param {PhoneNumber} props.selectedNumber - Currently selected number
 * @param {PhoneNumberConfig} props.config - Module configuration
 * @param {boolean} props.searchPerformed - Whether search has been performed
 * @returns {React.ReactElement} Search results component
 */
const SearchResults = ({
  results,
  isSearching,
  onNumberSelect,
  isPurchasing,
  selectedNumber,
  config,
  searchPerformed
}) => {
  // Sort results by price and location
  const sortedResults = useMemo(() => {
    return [...results].sort((a, b) => {
      // First sort by whether monthly fee is included
      const aIncluded = a.pricing?.monthly?.isIncluded || false;
      const bIncluded = b.pricing?.monthly?.isIncluded || false;
      
      if (aIncluded !== bIncluded) {
        return bIncluded ? 1 : -1; // Included plans first
      }

      // Then sort by monthly cost
      const aCost = a.pricing?.monthly?.amount || 0;
      const bCost = b.pricing?.monthly?.amount || 0;
      
      if (aCost !== bCost) {
        return aCost - bCost;
      }

      // Finally sort by phone number
      return a.phoneNumber.localeCompare(b.phoneNumber);
    });
  }, [results]);

  // Loading state
  if (isSearching) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex items-center justify-center">
          <svg className="animate-spin h-8 w-8 text-teal-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600 dark:text-gray-300 dark:text-gray-600">Searching for available numbers...</span>
        </div>
      </div>
    );
  }

  // No results state
  if (searchPerformed && results.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8 dark:bg-gray-800 dark:border-gray-700">
        <div className="text-center">
          <div className="text-4xl mb-4">📞</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-white">No Numbers Found</h3>
          <p className="text-gray-600 dark:text-gray-300 dark:text-gray-600">
            No phone numbers match your search criteria. Try adjusting your filters or search in a different area.
          </p>
        </div>
      </div>
    );
  }

  // Results display
  return (
    <div className="bg-white border border-gray-200 rounded-lg mt-6 dark:bg-gray-800 dark:border-gray-700">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Available Numbers ({results.length})
        </h3>
        {config.context !== PhoneNumberContext.ADMIN && (
          <p className="text-sm text-gray-600 mt-1 dark:text-gray-300 dark:text-gray-600">
            Select a number to purchase or add to your account
          </p>
        )}
      </div>

      <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
        {sortedResults.map((number, index) => (
          <PhoneNumberCard
            key={`${number.phoneNumber}-${index}`}
            number={number}
            onSelect={() => onNumberSelect(number)}
            isPurchasing={isPurchasing && selectedNumber?.phoneNumber === number.phoneNumber}
            config={config}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Individual Phone Number Card Component
 */
const PhoneNumberCard = ({ number, onSelect, isPurchasing, config }) => {
  const costInfo = useMemo(() => calculateTotalCost(number), [number]);

  return (
    <div className="px-6 py-4 hover:bg-gray-50 transition-colors dark:bg-gray-900">
      <div className="flex justify-between items-center">
        {/* Number Information */}
        <div className="flex-1">
          <div className="font-mono text-lg font-medium text-gray-900 dark:text-white">
            {formatPhoneNumber(number.phoneNumber)}
          </div>
          
          {/* Location */}
          <div className="text-sm text-gray-600 mt-1 dark:text-gray-300 dark:text-gray-600">
            {number.locality && `${number.locality}, `}
            {number.region && `${number.region}, `}
            {number.country}
          </div>

          {/* Capabilities */}
          {number.capabilities && (
            <div className="flex flex-wrap gap-2 mt-2">
              {number.capabilities.voice && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                  </svg>
                  Voice
                </span>
              )}
              {number.capabilities.sms && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                  </svg>
                  SMS
                </span>
              )}
              {number.capabilities.mms && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                  MMS
                </span>
              )}
            </div>
          )}

          {/* Pricing Information */}
          {number.pricing && (
            <div className="mt-3 text-sm">
              <div className="flex items-center space-x-4">
                <div>
                  <span className="text-gray-500">Monthly: </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {costInfo.isIncluded ? (
                      <span className="text-green-600">Included in Plan</span>
                    ) : (
                      `$${costInfo.monthlyFee.toFixed(2)}/month`
                    )}
                  </span>
                </div>
                {costInfo.setupFee > 0 && (
                  <div>
                    <span className="text-gray-500">Setup: </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ${costInfo.setupFee.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
              
              {!costInfo.isIncluded && (
                <div className="text-xs text-gray-500 mt-1">
                  First month total: ${costInfo.total.toFixed(2)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Button */}
        <div className="ml-6">
          <button
            onClick={onSelect}
            disabled={isPurchasing}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors flex items-center space-x-2 min-w-[100px] justify-center dark:text-white"
          >
            {isPurchasing ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Processing...</span>
              </>
            ) : (
              <span>
                {config.context === PhoneNumberContext.ADMIN ? 'Purchase' : 'Select'}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
